/**
 * Register Page
 * 
 * User registration interface for the Prelude application.
 * Admin registration has been moved to the mobile app.
 * This page now only supports member/musician/leader registration.
 * When users try to register as admin, they are shown download links.
 */

import React, { useState } from 'react';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  Card,
  CardContent,
  Container,
  Grid,
  Link,
  Radio,
  FormControlLabel,
  Stepper,
  Step,
  StepLabel,
  TextField,
  Typography,
  Alert,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Tooltip,
  Zoom,
  Paper,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import InfoIcon from '@mui/icons-material/Info';
import PhoneIphoneIcon from '@mui/icons-material/PhoneIphone';
import { useAuth } from '../context/AuthContext';
import axios from '../utils/axios';
import { API_ENDPOINTS } from '../config/api';

const Register = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { login } = useAuth();
  const [activeStep, setActiveStep] = useState(0);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [verifiedChurch, setVerifiedChurch] = useState(null);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    confirmPassword: '',
    role: '',
    churchCode: '',
  });

  const steps = [
    t('register.steps.account'),
    t('register.steps.role'),
    t('register.steps.payment')
  ];

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (field === 'role') {
      setVerifiedChurch(null);
      setFormData(prev => ({ 
        ...prev, 
        [field]: value,
        churchCode: '',
      }));
    }
  };

  const verifyChurchCode = async (code) => {
    try {
      const response = await axios.get(`${API_ENDPOINTS.CHURCHES.VERIFY}/${code}`);
      if (response.data && response.data.church) {
        setVerifiedChurch(response.data.church);
        setFormData(prev => ({ 
          ...prev,
          churchId: response.data.church._id
        }));
        setError('');
        return true;
      }
      setError(t('register.errors.invalidChurchCode'));
      return false;
    } catch (err) {
      setVerifiedChurch(null);
      setError(t('register.errors.invalidChurchCode'));
      return false;
    }
  };

  const handleRegister = async (registrationData) => {
    try {
      const response = await axios.post(API_ENDPOINTS.AUTH.REGISTER, registrationData);
      if (!response.data.success) {
        throw new Error(response.data.message || 'Registration failed');
      }
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || error.message || 'Registration failed');
    }
  };

  const handleLogin = async (email, password) => {
    try {
      const response = await login(email, password);
      if (!response.success) {
        throw new Error(response.message || 'Login failed');
      }
      return response;
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || 'Login failed';
      throw new Error(errorMessage);
    }
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);
      setError('');

      if (verifiedChurch) {
        formData.churchId = verifiedChurch.id;
      }

      const userData = {
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        password: formData.password,
        role: formData.role,
        churchCode: formData.churchCode,
        churchId: formData.churchId,
      };

      const response = await handleRegister(userData);
      if (response.success) {
        try {
          const loginResponse = await handleLogin(formData.email, formData.password);
          if (loginResponse) {
            navigate('/app');
          } else {
            navigate('/login');
          }
        } catch (err) {
          navigate('/login');
        }
      } else {
        setError(response.message || t('register.errors.registrationFailed'));
      }
    } catch (err) {
      setError(t('register.errors.registrationFailed'));
    } finally {
      setLoading(false);
    }
  };

  const handleNext = async () => {
    setError('');
    
    if (activeStep === 1 && ['musician', 'leader', 'member'].includes(formData.role) && formData.churchCode) {
      const isValid = await verifyChurchCode(formData.churchCode);
      if (!isValid) return;
    }

    if (!validateStep()) return;

    if (activeStep === steps.length - 1) {
      await handleSubmit();
      return;
    }
    
    setActiveStep(prevStep => prevStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prev) => prev - 1);
    setError('');
  };

  const validateStep = () => {
    switch (activeStep) {
      case 0:
        if (!formData.firstName || !formData.lastName || !formData.email || !formData.password || !formData.confirmPassword) {
          setError(t('register.errors.requiredField'));
          return false;
        }
        if (formData.password !== formData.confirmPassword) {
          setError(t('register.errors.passwordMismatch'));
          return false;
        }
        if (formData.password.length < 8) {
          setError(t('register.errors.passwordTooShort'));
          return false;
        }
        return true;

      case 1:
        if (!formData.role) {
          setError(t('common.errors.allFieldsRequired'));
          return false;
        }
        if (['musician', 'leader', 'member'].includes(formData.role)) {
          if (!formData.churchCode) {
            setError(t('register.errors.churchCodeRequired'));
            return false;
          }
        }
        return true;

      case 2:
        return true;

      default:
        return true;
    }
  };

  const renderPersonalInfoStep = () => (
    <>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6}>
          <TextField
            required
            fullWidth
            label={t('register.firstName')}
            value={formData.firstName}
            onChange={(e) => handleInputChange('firstName', e.target.value)}
            error={!!error && !formData.firstName}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            required
            fullWidth
            label={t('register.lastName')}
            value={formData.lastName}
            onChange={(e) => handleInputChange('lastName', e.target.value)}
            error={!!error && !formData.lastName}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            required
            fullWidth
            type="email"
            label={t('register.email')}
            value={formData.email}
            onChange={(e) => handleInputChange('email', e.target.value)}
            error={!!error && !formData.email}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            required
            fullWidth
            type="password"
            label={t('register.password')}
            value={formData.password}
            onChange={(e) => handleInputChange('password', e.target.value)}
            error={!!error && (!formData.password || formData.password.length < 8)}
            helperText={formData.password && formData.password.length < 8 ? t('register.errors.passwordTooShort') : ''}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            required
            fullWidth
            type="password"
            label={t('register.confirmPassword')}
            value={formData.confirmPassword}
            onChange={(e) => handleInputChange('confirmPassword', e.target.value)}
            error={!!error && (!formData.confirmPassword || formData.password !== formData.confirmPassword)}
            helperText={formData.confirmPassword && formData.password !== formData.confirmPassword ? t('register.errors.passwordMismatch') : ''}
          />
        </Grid>
      </Grid>
    </>
  );

  const renderRoleStep = () => (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <Typography variant="h6" gutterBottom>
          {t('register.plans.title')}
        </Typography>
        <Alert severity="info" sx={{ mb: 3 }}>
          {t('register.plans.freeForMusicians')}
        </Alert>
      </Grid>
      
      <Grid container item xs={12} spacing={2}>
        {['musician', 'leader'].map((role) => (
          <Grid item xs={12} md={6} key={role}>
            <Card
              variant="outlined"
              sx={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                border: formData.role === role ? 2 : 1,
                borderColor: formData.role === role ? 'primary.main' : 'divider',
                transition: 'all 0.2s ease-in-out',
                backgroundColor: 'background.paper',
                color: 'text.primary',
                '&:hover': {
                  borderColor: 'primary.main',
                  transform: 'translateY(-2px)',
                  boxShadow: 1,
                },
                cursor: 'pointer',
              }}
              onClick={() => handleInputChange('role', role)}
            >
              <CardContent sx={{ p: 2, flexGrow: 1 }}>
                <FormControlLabel
                  value={role}
                  control={<Radio checked={formData.role === role} />}
                  label={
                    <Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                        <Typography variant="h6" color="primary" sx={{ fontWeight: 'bold' }}>
                          {t(`register.plans.${role}.title`)}
                        </Typography>
                      </Box>
                      <Typography variant="subtitle1" color="primary.main" sx={{ mb: 1 }}>
                        {t(`register.plans.${role}.price`)}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        {t(`register.plans.${role}.description`)}
                      </Typography>
                      <List dense disablePadding>
                        {t(`register.plans.${role}.features`, { returnObjects: true }).map((feature, index) => (
                          <ListItem key={index} disableGutters sx={{ py: 0.5 }}>
                            <ListItemIcon sx={{ minWidth: 28 }}>
                              <CheckCircleIcon color="primary" sx={{ fontSize: 16 }} />
                            </ListItemIcon>
                            <ListItemText 
                              primary={feature}
                              primaryTypographyProps={{ 
                                variant: 'body2',
                                sx: { lineHeight: 1.3 }
                              }}
                            />
                          </ListItem>
                        ))}
                      </List>
                    </Box>
                  }
                  sx={{
                    margin: 0,
                    width: '100%',
                    alignItems: 'flex-start',
                    '.MuiFormControlLabel-label': {
                      width: '100%'
                    }
                  }}
                />
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Admin Banner — Download App */}
      <Grid item xs={12} sx={{ mt: 2 }}>
        <Paper
          elevation={0}
          sx={{
            p: 3,
            borderRadius: 3,
            background: 'linear-gradient(135deg, rgba(162,89,255,0.15) 0%, rgba(255,183,77,0.1) 100%)',
            border: '1px solid rgba(162,89,255,0.3)',
            textAlign: 'center',
          }}
        >
          <PhoneIphoneIcon sx={{ fontSize: 40, color: '#a259ff', mb: 1 }} />
          <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1 }}>
            {t('register.downloadApp.title', 'Want to register your church?')}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t('register.downloadApp.description', 'Admin registration is available exclusively on the Prelude app.')}
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
            <Button
              variant="contained"
              href="https://apps.apple.com/app/prelude"
              target="_blank"
              startIcon={<span>🍎</span>}
              sx={{ 
                bgcolor: '#000', color: '#fff', borderRadius: '12px', px: 3,
                '&:hover': { bgcolor: '#333' }
              }}
            >
              App Store
            </Button>
            <Button
              variant="contained"
              href="https://play.google.com/store/apps/details?id=com.prelude"
              target="_blank"
              startIcon={<span>▶️</span>}
              sx={{ 
                bgcolor: '#01875f', color: '#fff', borderRadius: '12px', px: 3,
                '&:hover': { bgcolor: '#016b4d' }
              }}
            >
              Google Play
            </Button>
          </Box>
        </Paper>
      </Grid>

      <Grid item xs={12} sx={{ mt: 3 }}>
        <TextField
          required
          fullWidth
          name="churchCode"
          label={t('register.churchCode')}
          value={formData.churchCode}
          onChange={(e) => handleInputChange('churchCode', e.target.value)}
          helperText={t('register.churchCodeHelp')}
          InputProps={{
            endAdornment: (
              <Tooltip 
                title={t('register.churchCodeHelp')}
                TransitionComponent={Zoom}
              >
                <InfoIcon color="action" sx={{ ml: 1 }} />
              </Tooltip>
            ),
          }}
        />
      </Grid>
    </Grid>
  );

  const renderPaymentStep = () => (
    <Box sx={{ mt: 2 }}>
      <Typography>{t('register.noPaymentRequired')}</Typography>
    </Box>
  );

  const renderStepContent = (step) => {
    switch (step) {
      case 0:
        return renderPersonalInfoStep();
      case 1:
        return renderRoleStep();
      case 2:
        return renderPaymentStep();
      default:
        return null;
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        backgroundColor: 'primary.main',
        display: 'flex',
        alignItems: 'center',
        py: 4,
        color: 'text.primary',
      }}
    >
      <Container maxWidth="lg">
        <Card
          sx={{
            p: 4,
            backgroundColor: '#1a1a1a',
            backdropFilter: 'blur(10px)',
          }}
        >
          <Box sx={{ width: '100%', mb: 4 }}>
            <Typography variant="h4" component="h1" gutterBottom align="center" sx={{ fontWeight: 800 }}>
              {t('register.title')}
            </Typography>
            <Typography variant="body1" color="text.secondary" align="center" sx={{ mb: 4 }}>
              {t('register.subtitle')}
            </Typography>

            <Stepper activeStep={activeStep}>
              {steps.map((label, index) => (
                <Step key={label} completed={activeStep > index}>
                  <StepLabel>{label}</StepLabel>
                </Step>
              ))}
            </Stepper>
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {activeStep === 2 && verifiedChurch && (
            <Alert severity="success" sx={{ mt: 2 }}>
              {t('register.member.churchConfirmation', { churchName: verifiedChurch.name })}
            </Alert>
          )}

          <Box sx={{ mt: 2 }}>
            {renderStepContent(activeStep)}

            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
              <Button
                onClick={handleBack}
                disabled={activeStep === 0 || loading}
              >
                {t('common.back')}
              </Button>

              {activeStep === steps.length - 1 ? (
                <Button
                  variant="contained"
                  onClick={handleSubmit}
                  disabled={loading}
                  sx={{ mt: 3, ml: 1 }}
                >
                  {loading ? t('common.loading') : t('common.submit')}
                </Button>
              ) : (
                <Button
                  variant="contained"
                  onClick={handleNext}
                  disabled={loading}
                  sx={{ mt: 3, ml: 1 }}
                >
                  {loading ? t('common.loading') : t('common.next')}
                </Button>
              )}
            </Box>
          </Box>

          <Box sx={{ mt: 2, textAlign: 'center' }}>
            <Link
              component={RouterLink}
              to="/login"
              variant="body2"
              sx={{
                color: 'primary.main',
                textDecoration: 'none',
                '&:hover': {
                  textDecoration: 'underline',
                },
              }}
            >
              {t('auth.alreadyHaveAccount')}
            </Link>
          </Box>
        </Card>
      </Container>
    </Box>
  );
};

export default Register;
